<template>
  <div class="initial-control">
    <div class="dropset-options">
      <div class="dropset-switch-container">
        <md-switch
          :value="dripSetToggleValue"
          class="md-primary ui-theme-3"
          :disabled="dripSetToggleDisabled"
          @change="value => updateField('isDropSetEnabled', value)"
        />
        <label>Drip Set</label>
        <help-tooltip v-if="dripSetEnabledError" type="error">
          {{ dripSetEnabledError.message }}
        </help-tooltip>
        <help-tooltip v-if="dripSetOrPumpError" type="error">
          {{ dripSetOrPumpError.message }}
        </help-tooltip>
      </div>

      <div class="dropset-options-content">
        <div>
          <label>Rate</label>
          <help-tooltip v-if="dripSetRateError" type="error">
            {{ dripSetRateError.message }}
          </help-tooltip>
          <help-tooltip v-if="dripSetRateRangeError" type="error">
            {{ dripSetRateRangeError.message }}
          </help-tooltip>
          <md-field>
            <md-input
              :value="initialMedication.rate || ''"
              :disabled="dripSetControlsDisabled"
              @input="
                value => updateField('rate', parseInt(value, 10) || 0)
              "
            />

            <span class="md-suffix">drops/min</span>
          </md-field>
        </div>
        <div>
          <label>Drip Set</label>
          <help-tooltip v-if="dripSetSelectedError" type="error">
            {{ dripSetSelectedError.message }}
          </help-tooltip>
          <md-field>
            <md-select
              :value="initialMedication.dripSet"
              :disabled="dripSetControlsDisabled"
              placeholder="Drip Set"
              @input="dripSet => updateField('dripSet', dripSet)"
            >
              <md-option
                v-for="tubing in availableTubings"
                :key="tubing.key"
                :value="tubing.key"
              >
                {{ tubing.label.en }}
              </md-option>
            </md-select>
          </md-field>
        </div>
      </div>
    </div>

    <md-divider />

    <div :class="{
      'ivpump-options-parent': parentBag,
      'ivpump-options': !parentBag
    }"
    >
      <div class="ivpump-switch-container">
        <md-switch
          :value="pumpToggleValue"
          :disabled="pumpToggleDisabled"
          class="md-primary ui-theme-3"
          @change="value => updateField('isIvPumpEnabled', value)"
        />
        <label>IV Pump</label>
        <help-tooltip v-if="isPumpEnabledError" type="error">
          {{ isPumpEnabledError.message }}
        </help-tooltip>
      </div>
      <div v-if="parentBag">
        Rate is controlled by Primary Infusion
      </div>
      <div v-else class="ivpump-inner" style="flex: 1">
        <initial-pump-settings
          :pump-settings="initialMedication.ivPumpPrimary || {}"
          :is-enabled="!!initialMedication.isIvPumpEnabled && initialMedication.volume && !readonly"
          :concentration="selectedConcentration"
          :formularies="availableFormularies"
          :bag-volume="initialMedication.volume || 'NONE'"
          :errors="pumpPrimaryErrors"
          :readonly="readonly"
          @update="value => updateField('ivPumpPrimary', value)"
        />
        <div
          v-if="caseConf.iv.pump.allowOptions"
          class="ivpump-switch-container"
        >
          <md-switch
            :value="initialMedication.isIvPumpEnabled && initialMedication.isIvPumpSecondary"
            :disabled="!initialMedication.isIvPumpEnabled || !caseConf.iv.pump.allowOptions || readonly"
            class="md-primary ui-theme-3"
            @change="value => updateField('isIvPumpSecondary', value)"
          />
          <label>Set secondary rates</label>
        </div>
        <initial-pump-settings
          v-if="caseConf.iv.pump.allowOptions && initialMedication.isIvPumpSecondary"
          :pump-settings="initialMedication.ivPumpSecondary || {}"
          :is-enabled="!!initialMedication.isIvPumpEnabled && !readonly"
          :concentration="selectedConcentration"
          :formularies="availableFormularies"
          :bag-volume="initialMedication.volume"
          :errors="pumpSecondaryErrors"
          @update="value => updateField('ivPumpSecondary', value)"
        />
        <div
          v-if="caseConf.iv.pump.allowOptions && initialMedication.isIvPumpSecondary"
          class="ivpump-switch-container"
        >
          <md-switch
            :value="initialMedication.isIvPumpEnabled && initialMedication.isIvPumpSecondaryActive"
            :disabled="!caseConf.iv.pump.enabled || readonly"
            class="md-primary ui-theme-3"
            @change="value => updateField('isIvPumpSecondaryActive', value)"
          />
          <label>Secondary active at start</label>
        </div>
      </div>
    </div>
    <md-divider />

    <div class="start-time-options">
      <div class="already-running-switch-container">
        <md-switch
          :value="runningToggleValue"
          :disabled="runningToggleDisabled"
          class="md-primary ui-theme-3"
          @change="value => updateField('isAlreadyRunning', value)"
        />
        <label>Already running ?</label>
      </div>

      <div v-if="parentBag">
        Start time is controlled by Primary Infusion
      </div>
      <div v-else class="start-time-options-content">
        <label>
          <help-tooltip v-if="priorToStartError" type="error">
            {{ priorToStartError.message }}
          </help-tooltip>
          <help-tooltip v-if="alreadyRunningError" type="error">
            {{ alreadyRunningError.message }}
          </help-tooltip>
          <help-tooltip v-if="childBeforeParentError" type="error">
            {{ childBeforeParentError.message }}
          </help-tooltip>
          When entering scenario, this infusion has been running for
        </label>
        <div class="start-time-inputs">
          <md-field>
            <md-input
              placeholder="hh"
              type="number"
              :min="0"
              :value="initialMedication.runningForHour"
              :disabled="!initialMedication.isAlreadyRunning || readonly"
              @input="
                value =>
                  updateField('runningForHour', parseInt(value, 10) || 0)
              "
            />
          </md-field>
          <span>:&nbsp;</span>
          <md-field>
            <md-input
              placeholder="mm"
              type="number"
              :min="0"
              :max="59"
              :value="initialMedication.runningForMinutes"
              :disabled="!initialMedication.isAlreadyRunning || readonly"
              @input="
                value =>
                  updateField(
                    'runningForMinutes',
                    Math.min(Math.max(parseInt(value, 10) || 0, 0), 59)
                  )
              "
            />
          </md-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';

import { tubings } from '~lib/common/config/iv';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import InitialPumpSettings from './InitialPumpSettings.vue';

const DEFAULT_CONCENTRATION = {
  numeratorValue: 1,
  numeratorUnits: 'WEIGHT_MG',
  denominatorValue: 1,
  denominatorUnits: 'VOLUME_ML',
};

export default {
  name: 'initial-control',
  components: {
    InitialPumpSettings,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    initialMedication: {
      type: Object,
      required: true,
    },
    parentBag: {
      type: Object,
      default: () => null,
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    availableTubings() {
      if (this.caseConf.iv.enabled) {
        const availableDripSets = this.caseConf.iv.dropSet.dripSet;
        return tubings.filter(tubing => {
          return availableDripSets.includes(tubing.key);
        });
      }
      return [];
    },
    availableFormularies() {
      let formularies = [];
      if (this.initialMedication.treatmentType === 'MEDICATION') {
        formularies = this.selectedDosageFormInfusionRoute
          ? (this.selectedDosageFormInfusionRoute.options.formularies || [])
          : [];
      } else {
        formularies = this.selectedFluid ? this.selectedFluid.formularies || []: [];
      }
      formularies = cloneDeep(formularies);
      return formularies;
    },
    dripSetToggleValue() {
      return this.parentBag ? this.parentBag.isDropSetEnabled : this.initialMedication.isDropSetEnabled;
    },
    dripSetToggleDisabled() {
      return this.readonly || !!this.parentBag;
    },
    dripSetControlsDisabled() {
      return this.readonly ||
        this.parentBag ? !this.parentBag.isDropSetEnabled : !this.initialMedication.isDropSetEnabled;
    },
    pumpToggleValue() {
      return this.caseConf.iv.pump.enabled &&
        this.parentBag ? this.parentBag.isIvPumpEnabled : this.initialMedication.isIvPumpEnabled;
    },
    pumpToggleDisabled() {
      return !this.caseConf.iv.pump.enabled || this.readonly || !!this.parentBag || !this.initialMedication.volume;
    },
    runningToggleValue() {
      return this.parentBag ? this.parentBag.isAlreadyRunning : this.initialMedication.isAlreadyRunning;
    },
    runningToggleDisabled() {
      return this.readonly || !!this.parentBag;
    },
    selectedDosageForm() {
      const { medOrFluidId: medicationId } = this.initialMedication;
      if (!this.caseConf) {
        return null;
      }
      return this.caseConf.dosageForms.find(
        caseDosageForm => caseDosageForm.medicationId === medicationId,
      );
    },
    selectedFluid() {
      const { medOrFluidId: fluidKey } = this.initialMedication;
      if (!this.caseConf) {
        return null;
      }
      return this.caseConf.fluids.find(fluid => fluid.key === fluidKey);
    },
    selectedDosageFormInfusionRoute() {
      if (!this.selectedDosageForm) {
        return null;
      }
      return this.selectedDosageForm.enabledRoutes.find(
        route => route.key === 'IV_IO_INFUSION',
      );
    },
    selectedConcentration() {
      let concentration;
      if (this.initialMedication.treatmentType === 'FLUID') {
        // 1/1 for fluids
        concentration = DEFAULT_CONCENTRATION
      } else {
        // MEDICATION
        concentration = this.selectedDosageForm ?
          this.selectedDosageForm.concentration :
          DEFAULT_CONCENTRATION;
      }
      return concentration;
    },

    alreadyRunningError() {
      return this.errors.find(error => error.metadata.type === 'alreadyRunning');
    },
    priorToStartError() {
      return this.errors.find(error => error.metadata.type === 'priorToStart');
    },
    dripSetOrPumpError() {
      return this.errors.find(error => error.metadata.type === 'dripSetOrPump');
    },
    dripSetEnabledError() {
      return this.errors.find(error => error.metadata.type === 'dripSetEnabled');
    },
    dripSetSelectedError() {
      return this.errors.find(error => error.metadata.type === 'dripSetSelected');
    },
    dripSetRateError() {
      return this.errors.find(error => error.metadata.type === 'dripSetRate');
    },
    dripSetRateRangeError() {
      return this.errors.find(error => error.metadata.type === 'dripSetRateRange');
    },
    isPumpEnabledError() {
      return this.errors.find(error => error.metadata.type === 'isPumpEnabled');
    },
    pumpPrimaryErrors() {
      const primaryErrors = this.errors.filter(
        error => error.metadata.type.startsWith('pump') && error.metadata.isPrimary
      );
      return keyBy(primaryErrors, 'metadata.type');
    },
    pumpSecondaryErrors() {
      const secondaryErrors = this.errors.filter(
        error => error.metadata.type.startsWith('pump') && !error.metadata.isPrimary
      );
      return keyBy(secondaryErrors, 'metadata.type');
    },
    childBeforeParentError() {
      return this.errors.find(error => error.metadata.type === 'childBeforeParent');
    }
  },
  methods: {
    updateField(field, value) {
      this.$emit('update', field, value);
    }
  }
}
</script>

<style scoped>
.md-divider {
  margin-top: 24px;
  margin-bottom: 24px;
}

.ivpump-options-parent {
  display: flex;
  align-items: center;
}

.dropset-options,
.ivpump-options,
.start-time-options {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.dropset-switch-container,
.ivpump-switch-container,
.already-running-switch-container {
  min-width: 200px;
  display: flex;
  align-items: center;
}
.ivpump-options {
  align-items: flex-start;
}

.ivpump-inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-items: flex-start;
}

.dropset-options-content {
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.start-time-options-content {
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
}

.md-field.md-theme-default .md-suffix {
  padding: 0 10px;
  border: solid 1px #d2d2d2;
  background-color: #f3f3f3;
  color: #2b2b2b;
  border-radius: 5px;
}

.start-time-inputs {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-left: 8px;
}

.start-time-inputs .md-field {
  margin-top: 0;
  width: 80px;
}

.start-time-inputs .md-field input {
  min-width: 0;
}
</style>